import { Instagram, Facebook, Youtube, MessageCircle, MapPin, Mail, Clock } from "lucide-react";
import MotionPreferenceToggle from "@/components/MotionPreferenceToggle";

const Footer = () => {
  return (
    <footer
      className="text-[hsl(40_33%_94%)]"
      style={{
        background:
          "linear-gradient(165deg, hsl(150 35% 11%) 0%, hsl(150 32% 9%) 45%, hsl(25 30% 8%) 100%)",
      }}
    >
      <div className="container mx-auto px-6 pt-20 pb-10 md:pt-24 md:pb-12" style={{ maxWidth: "1300px" }}>
        {/* Editorial identity band — closing reflection */}
        <div className="text-center mb-16 md:mb-20 max-w-2xl mx-auto">
          <p className="font-sans text-[11px] tracking-[0.42em] uppercase text-accent mb-5">
            A Caribbean Legacy House
          </p>
          <h2 className="font-serif text-3xl md:text-[2.5rem] text-[hsl(40_33%_97%)] leading-[1.05] mb-6">
            Rooted Memory. <span className="italic text-accent">Living Future.</span>
          </h2>
          <p className="font-serif italic text-[hsl(40_25%_82%)]/90 text-base md:text-lg leading-relaxed">
            Thank you for visiting our Legacy House.
            <br />
            May these stories inspire moments of care, curiosity and connection
            in your own home.
          </p>
        </div>

        <div
          aria-hidden
          className="h-px w-full mb-14"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, hsl(40 55% 55% / 0.5) 50%, transparent 100%)",
          }}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-12">
          {/* Column 1 — Brand mini story */}
          <div>
            <h3 className="font-serif text-xl mb-4 text-[hsl(40_33%_97%)]">Sugar Town Organics</h3>
            <p className="text-sm font-sans leading-relaxed text-[hsl(40_25%_82%)] mb-6">
              A Caribbean Legacy House preserving the botanical wisdom, living
              traditions and everyday rituals of St. Kitts &amp; Nevis — shared
              in bottles, meals, gatherings and stories.
            </p>
            <div className="flex items-center gap-3">
              {[
                { icon: Instagram, href: "https://instagram.com/sugartownorganics", label: "Instagram" },
                { icon: Facebook, href: "https://facebook.com/sugartownorganics", label: "Facebook" },
                { icon: Youtube, href: "https://youtube.com/@sugartownorganics", label: "YouTube" },
                { icon: MessageCircle, href: "https://wa.me/18696686713", label: "WhatsApp" },
              ].map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-10 h-10 rounded-full border border-[hsl(40_33%_94%)]/20 flex items-center justify-center text-[hsl(40_33%_94%)]/80 hover:bg-accent hover:text-accent-foreground hover:border-accent hover:-translate-y-0.5 transition-all duration-300"
                >
                  <Icon className="w-[15px] h-[15px]" strokeWidth={1.5} />
                </a>
              ))}
            </div>
          </div>

          {/* Column 2 — Shop collections */}
          <div>
            <h4 className="font-sans font-semibold text-[11px] uppercase tracking-[0.28em] mb-5 text-accent">Shop Collections</h4>
            <ul className="space-y-3 text-sm font-sans text-[hsl(40_25%_82%)]">
              <li><a href="/shop?category=gift-boxes" className="hover:text-accent transition-colors">Island Gift Boxes</a></li>
              <li><a href="/taste-st-kitts" className="hover:text-accent transition-colors">Taste St. Kitts Treats</a></li>
              <li><a href="/brands/yaphene" className="hover:text-accent transition-colors">Yaphene Botanical Body Care</a></li>
              <li><a href="/experiences/balm-and-bond" className="hover:text-accent transition-colors">Balm &amp; Bond Experiences</a></li>
              <li><a href="/brands/anaelli" className="hover:text-accent transition-colors">AnaElli Digital Journals</a></li>
              <li><a href="/wholesale" className="hover:text-accent transition-colors">Corporate &amp; Hotel Gifting</a></li>
            </ul>
          </div>

          {/* Column 3 — Quick links */}
          <div>
            <h4 className="font-sans font-semibold text-[11px] uppercase tracking-[0.28em] mb-5 text-accent">Quick Links</h4>
            <ul className="space-y-3 text-sm font-sans text-[hsl(40_25%_82%)]">
              <li><a href="/visit-di-organics-shoppe" className="hover:text-accent transition-colors">Visit Di Organics Shoppe</a></li>
              <li><a href="/track-order" className="hover:text-accent transition-colors">Island Pickup Orders</a></li>
              <li><a href="/wholesale" className="hover:text-accent transition-colors">Wholesale Inquiry</a></li>
              <li><a href="/tour-group-workshops" className="hover:text-accent transition-colors">Tour Group Workshops</a></li>
              <li><a href="/company-profile" className="hover:text-accent transition-colors">Company Profile</a></li>
              <li><a href="/company-timeline" className="hover:text-accent transition-colors">Company Timeline</a></li>
              <li><a href="/history-and-heritage" className="hover:text-accent transition-colors">History &amp; Heritage</a></li>
              <li><a href="/archive" className="hover:text-accent transition-colors">The Living Archive of St. Kitts &amp; Nevis</a></li>
              <li><a href="/root-rewards-network" className="hover:text-accent transition-colors">ROOT Rewards Network</a></li>
              <li><a href="/faq" className="hover:text-accent transition-colors">Shipping Information</a></li>
              <li><a href="/contact" className="hover:text-accent transition-colors">Contact Us</a></li>
            </ul>
          </div>

          {/* Column 4 — Island contact */}
          <div>
            <h4 className="font-sans font-semibold text-[11px] uppercase tracking-[0.28em] mb-5 text-accent">Visit &amp; Contact</h4>
            <ul className="space-y-3 text-sm font-sans text-[hsl(40_25%_82%)]">
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-accent" />
                <a
                  href="https://maps.google.com/?q=Di+Organics+Shoppe+Shadwell+Great+House+St+Kitts"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-accent transition-colors"
                >
                  Di Organics Shoppe<br />Shadwell Great House<br />St. Kitts, West Indies
                </a>
              </li>
              <li className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4 flex-shrink-0 text-accent" />
                <a href="https://wa.me/18696686713" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors">WhatsApp Ordering</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 flex-shrink-0 text-accent" />
                <a href="mailto:sugartownorganics@gmail.com" className="hover:text-accent transition-colors">Email Contact</a>
              </li>
              <li className="flex items-center gap-2">
                <Clock className="w-4 h-4 flex-shrink-0 text-accent" />
                <span>Open Monday – Saturday</span>
              </li>
            </ul>
            <p className="mt-5 text-xs font-sans italic leading-relaxed text-[hsl(40_25%_72%)]/85">
              Visitors to St. Kitts can also pre-arrange curated island gift pickups and private Balm &amp; Bond sessions.
            </p>
          </div>
        </div>

        {/* Mandarin (中文) pages — aids discovery for visitors & search engines */}
        <div className="notranslate mt-14 pt-10 border-t border-[hsl(40_25%_82%)]/15" lang="zh">
          <h4 className="font-sans font-semibold text-[11px] uppercase tracking-[0.28em] mb-5 text-accent text-center">
            中文页面
          </h4>
          <ul className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm font-sans text-[hsl(40_25%_82%)]">
            <li><a href="/zh/discover-st-kitts" className="hover:text-accent transition-colors">走进圣基茨</a></li>
            <li><a href="/zh/our-story" className="hover:text-accent transition-colors">我们的故事</a></li>
            <li><a href="/zh/legacy-impact" className="hover:text-accent transition-colors">传承与影响力</a></li>
            <li><a href="/zh/visit-di-organics-shoppe" className="hover:text-accent transition-colors">到访 Di Organics Shoppe</a></li>
            <li><a href="/zh/experiences/balm-and-bond" className="hover:text-accent transition-colors">Balm &amp; Bond</a></li>
            <li><a href="/zh/experiences/sunrise-and-stillness" className="hover:text-accent transition-colors">Sunrise &amp; Stillness</a></li>
          </ul>
        </div>

        {/* Accessibility — manual motion control */}
        <div className="mt-14 pt-10 border-t border-[hsl(40_25%_82%)]/15 text-center flex flex-col items-center">
          <MotionPreferenceToggle />
        </div>




        <div className="border-t border-[hsl(40_25%_82%)]/15 mt-14 pt-6">
          <p className="text-xs text-[hsl(40_25%_82%)]/65 font-sans text-center">
            Proud member of the{" "}
            <a href="https://sknchamber.com" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors underline">Chamber of Industry and Commerce</a>{" "}
            &amp;{" "}
            <a href="https://helensdaughters.org" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors underline">Helen's Daughters</a>
          </p>
        </div>
      </div>

      {/* Micro trust strip */}
      <div className="bg-[hsl(150_32%_9%)] py-5 px-6">
        <div className="container mx-auto text-center" style={{ maxWidth: "1100px" }}>
          <p className="font-serif italic text-sm text-[hsl(40_25%_82%)]/85">
            Rooted Memory. Living Future.
          </p>
          <p className="text-[11px] mt-2 font-sans text-[hsl(40_25%_82%)]/55">
            © {new Date().getFullYear()} Sugar Town Organics. All rights reserved.
          </p>
          <p className="text-[11px] mt-2 font-sans text-[hsl(40_25%_82%)]/55">
            <a href="/account-native" className="hover:text-accent underline">My account</a>{" · "}
            <a href="/admin/login" className="hover:text-accent underline">Staff sign in</a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
